import { MouseEvent, useEffect, useState } from 'react'
import styles from './styles.module.scss'
import { Bottom, Fit, Top } from 'types/fit'
import Toggle from '@components/ui/Toggle'
import { FilterOption } from 'types/filters'
import { BUILD } from '@consts/index'
import { isSizeFilterType } from '@helpers/filters/isSizeFilterType'
import { useFit } from '@helpers/hooks/useFit'

type ToggleProps = {
  isToggled: boolean
  onToggle: (ev: MouseEvent<HTMLButtonElement>) => void
}

type SavedFitBoxHeaderProps = ToggleProps & {
  onViewFitBox: () => void
  isFitOpen: boolean
}

const SavedFitBoxHeader = ({
  isToggled,
  onViewFitBox,
  onToggle,
  isFitOpen
}: SavedFitBoxHeaderProps) => {
  return (
    <div className={styles.SavedFitBoxHeader}>
      <Toggle
        toggled={isToggled}
        onToggle={onToggle}
        labelA="Show only items in My Fit"
        labelB="Only items in My Fit"
      />

      <button
        className={`${styles.SavedFitBoxHeader__button} ${
          isToggled ? styles.SavedFitBoxHeader__button__on : ''
        }`}
        onClick={onViewFitBox}
      >
        {isFitOpen ? 'Close' : 'View'}
      </button>
    </div>
  )
}

function getFitOptionsByName(names: (keyof Fit)[], savedFit: Partial<Fit>) {
  return names.map(option => {
    return {
      name: option,
      value: savedFit[option]
    }
  })
}

type SavedFitBoxSizeOptionsProps = {
  category:
    | {
        type: string
        options: {
          name: keyof Top | keyof Bottom
          value: string | undefined
        }[]
      }
    | undefined
  isApplied: boolean
}

const SavedFitBoxSizeOptions = ({
  category,
  isApplied = false
}: SavedFitBoxSizeOptionsProps) => {
  const { type, options } = category || {}

  if (!options) return null

  return (
    <div className={styles.SavedFitBoxContent__categories}>
      <span
        className={`${styles.SavedFitBoxContent__title} ${
          isApplied ? styles.SavedFitBoxContent__title__isApplied : ''
        }`}
      >
        {type}
      </span>
      <div
        className={`${styles.SavedFitBoxContent__options} ${
          isApplied ? styles.SavedFitBoxContent__options__isApplied : ''
        }`}
      >
        {options.map(option => (
          <div className={styles.SavedFitBoxContent__option} key={option.name}>
            <span className={styles.SavedFitBoxContent__optionType}>
              {option.name}
            </span>
            <span className={styles.SavedFitBoxContent__optionValue}>
              {option.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export const SavedFitBoxContent = ({
  savedFit,
  isApplied = false
}: {
  savedFit: Partial<Fit>
  isApplied?: boolean
}) => {
  const savedFitCategories = [
    {
      type: 'Bottoms',
      options: getFitOptionsByName(['waist', 'build', 'leg'], savedFit)
    },
    {
      type: 'Tops',
      options: getFitOptionsByName(['size', 'cut', 'length'], savedFit)
    }
  ]

  return (
    <div
      className={`${styles.SavedFitBoxContent} ${
        isApplied ? styles.SavedFitBoxContent__isApplied : ''
      }`}
    >
      {savedFitCategories.map(category => (
        <SavedFitBoxSizeOptions
          category={category}
          key={category.type}
          isApplied={isApplied}
        />
      ))}
    </div>
  )
}

type SavedFitBoxProps = {
  savedFit: Partial<Fit>
  onApplyFitToggle: (state: boolean) => void
  isFitFilterToggleActive: boolean
  setIsFitFilterToggleActive: (state: boolean) => void
  selectedOptions: FilterOption[]
}

const EXCLUDE_SELECTED_OPTIONS = ['shortsleg', 'swimshortsleg']

const MIN_SAVED_FIT_OPTIONS = 2

function SavedFitBox({
  savedFit,
  selectedOptions,
  isFitFilterToggleActive,
  setIsFitFilterToggleActive,
  onApplyFitToggle
}: SavedFitBoxProps) {
  const { isSavedFitApplied } = useFit()

  const filteredSelectedOptions = selectedOptions.filter(
    option =>
      isSizeFilterType(option.type) &&
      !EXCLUDE_SELECTED_OPTIONS.includes(option.type)
  )

  const isSavedFitValid =
    filteredSelectedOptions.length > MIN_SAVED_FIT_OPTIONS &&
    filteredSelectedOptions.every(
      option =>
        savedFit[option.type as keyof Fit] ===
        String(option.value.replace(BUILD, '').trim())
    )

  const [isFitOpen, setIsFitOpen] = useState(false)

  const hasNoSavedFit = !Object.keys(savedFit).length

  const handleOnToggle = () => {
    onApplyFitToggle(!isFitFilterToggleActive)
  }

  const handleOnViewFitBox = () => setIsFitOpen(prev => !prev)

  useEffect(() => {
    setIsFitFilterToggleActive(isSavedFitValid)
  }, [
    isSavedFitApplied,
    isFitFilterToggleActive,
    setIsFitFilterToggleActive,
    isSavedFitValid
  ])

  if (hasNoSavedFit) return <></>

  return (
    <div
      className={`${styles.SavedFitBoxContainer} ${
        isFitFilterToggleActive ? styles.SavedFitBoxContainer__on : ''
      }`}
    >
      <SavedFitBoxHeader
        onToggle={handleOnToggle}
        isToggled={isFitFilterToggleActive}
        onViewFitBox={handleOnViewFitBox}
        isFitOpen={isFitOpen}
      />
      {isFitOpen ? (
        <SavedFitBoxContent
          savedFit={savedFit}
          isApplied={isFitFilterToggleActive}
        />
      ) : null}
    </div>
  )
}

export default SavedFitBox
